import { motion } from 'framer-motion'
import { Code, Share2, FileText, Palette, CheckCircle, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import SEO from '../components/SEO'
import FAQ from '../components/FAQ'

const Services = () => {
  const { t } = useLanguage()
  const services = [
    {
      icon: Code,
      title: 'Web Development',
      description: 'Custom websites and web applications tailored to your business needs',
      features: [
        'Responsive Design',
        'Modern Technologies (React, Next.js)',
        'Fast Loading Speed',
        'SEO Optimized',
        'E-commerce Solutions',
        'CMS Integration'
      ],
      gradient: 'from-gold-400 to-bronze-500'
    },
    {
      icon: Share2,
      title: 'Social Media Management',
      description: 'Strategic social media management to grow your online presence',
      features: [
        'Content Strategy',
        'Daily Posting & Scheduling',
        'Community Management',
        'Performance Analytics',
        'Multi-Platform Management',
        'Engagement Growth'
      ],
      gradient: 'from-bronze-400 to-gold-600'
    },
    {
      icon: FileText,
      title: 'Content Creation',
      description: 'Engaging content that tells your brand story and connects with your audience',
      features: [
        'Blog Posts & Articles',
        'Video Production',
        'Social Media Content',
        'Copywriting',
        'Photography',
        'Infographics'
      ],
      gradient: 'from-gold-500 to-bronze-400'
    },
    {
      icon: Palette,
      title: 'Logo Design',
      description: 'Unique brand identities and memorable logo designs',
      features: [
        'Custom Logo Design',
        'Brand Identity',
        'Multiple Concepts',
        'Unlimited Revisions',
        'All File Formats',
        'Brand Guidelines'
      ],
      gradient: 'from-bronze-500 to-gold-500'
    }
  ]

  return (
    <>
      <SEO 
        title={t('seo.services.title')}
        description={t('seo.services.description')}
        keywords={t('seo.services.keywords')}
      />
      <div className="mt-20">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark relative overflow-hidden">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.03, 0.08, 0.03] }}
          transition={{ duration: 20, repeat: Infinity }}
          className="absolute top-0 right-0 w-96 h-96 bg-gradient-gold rounded-full blur-3xl"
        />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="section-title mb-6">
              Our <span className="text-gradient-gold">Services</span>
            </h1>
            <p className="text-xl text-beige-200 max-w-3xl mx-auto">
              Comprehensive digital solutions tailored to your business needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark">
        <div className="container mx-auto px-4">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <motion.div
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    className={`w-24 h-24 rounded-3xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 shadow-gold-lg`}
                  >
                    <service.icon className="w-12 h-12 text-white" />
                  </motion.div>
                  <h2 className="text-4xl font-display font-bold text-cream mb-4">
                    {service.title}
                  </h2>
                  <p className="text-xl text-beige-200 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  <Link to="/contact" className="btn-primary inline-flex items-center space-x-2">
                    <span>Get Started</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="card-premium">
                    <h3 className="text-2xl font-display font-bold text-cream mb-6">
                      What's Included:
                    </h3>
                    <ul className="space-y-4">
                      {service.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex items-start space-x-3"
                        >
                          <CheckCircle className="w-6 h-6 text-gold-400 flex-shrink-0 mt-1" />
                          <span className="text-beige-200 text-lg">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-cream mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-beige-200 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help grow your business
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center space-x-2">
              <span>Contact Us Today</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />
    </div>
    </>
  )
}

export default Services
